import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config";

const DonatorDashboard = () => {
  const [donator, setDonator] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDonatorData = async () => {
      try {
        // Get current donator
        const meRes = await fetch(`${config.API_URL}/donators/me`, {
          credentials: "include",
        });

        if (!meRes.ok) {
          navigate("/donator-login");
          return;
        }

        const donatorData = await meRes.json();
        setDonator(donatorData);
        setProfileForm({ first_name: donatorData.first_name, last_name: donatorData.last_name });

        // Get saved payment methods
        const pmRes = await fetch(
          `${config.API_URL}/donators/${donatorData.id}/payment-methods`,
          { credentials: "include" }
        );

        if (pmRes.ok) {
          const methods = await pmRes.json();
          setPaymentMethods(methods);
        }
      } catch (err) {
        console.error("Error fetching donator data:", err);
        setError("Failed to load your information");
      } finally {
        setLoading(false);
      }
    };

    fetchDonatorData();
  }, [navigate]);

  const handleDeletePaymentMethod = async (pmId) => {
    if (!window.confirm("Remove this payment method?")) return;

    try {
      const res = await fetch(
        `${config.API_URL}/donators/${donator.id}/payment-methods/${pmId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (res.ok) {
        setPaymentMethods(paymentMethods.filter((pm) => pm.id !== pmId));
      }
    } catch (err) {
      console.error("Error deleting payment method:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${config.API_URL}/donators/logout`, {
        method: "POST",
        credentials: "include",
      });
      navigate("/donator-login");
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileError(null);
    try {
      const res = await fetch(`${config.API_URL}/donators/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileForm),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to update profile");
      }

      const updatedDonator = await res.json();
      setDonator(updatedDonator);
      setEditingProfile(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setProfileError(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const res = await fetch(`${config.API_URL}/donators/resend-verification`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        alert("Verification email sent! Please check your inbox.");
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to send verification email");
      }
    } catch (err) {
      console.error("Error resending verification:", err);
      alert("Failed to send verification email");
    } finally {
      setResendingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate("/donator-login")}
            className="text-blue-400 hover:text-blue-300"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">My Account</h1>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white text-sm"
          >
            Logout
          </button>
        </div>

        {/* Verification Banner */}
        {donator && !donator.is_verified && (
          <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-yellow-200">Email Not Verified</p>
                <p className="text-yellow-300 text-sm">
                  Please verify your email to access all features.
                </p>
              </div>
              <button
                onClick={handleResendVerification}
                disabled={resendingVerification}
                className="bg-yellow-600 hover:bg-yellow-500 px-4 py-2 rounded text-sm font-semibold transition disabled:opacity-50"
              >
                {resendingVerification ? "Sending..." : "Resend Email"}
              </button>
            </div>
          </div>
        )}

        {/* Profile Info */}
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Profile</h2>
            {!editingProfile && (
              <button
                onClick={() => setEditingProfile(true)}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Edit
              </button>
            )}
          </div>

          {editingProfile ? (
            <div className="space-y-4">
              {profileError && (
                <p className="text-red-500 text-sm">{profileError}</p>
              )}
              <div>
                <label className="block text-gray-400 text-sm mb-1">First Name</label>
                <input
                  type="text"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Last Name</label>
                <input
                  type="text"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Email</label>
                <p className="text-gray-500">{donator?.email}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setEditingProfile(false);
                    setProfileForm({ first_name: donator.first_name, last_name: donator.last_name });
                    setProfileError(null);
                  }}
                  className="px-4 py-2 border border-gray-600 rounded text-gray-300 hover:bg-gray-800 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded font-semibold transition disabled:opacity-50"
                >
                  {savingProfile ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p>
                <span className="text-gray-400">Name:</span>{" "}
                {donator?.first_name} {donator?.last_name}
              </p>
              <p>
                <span className="text-gray-400">Email:</span> {donator?.email}
              </p>
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 mb-6">
          <h2 className="text-lg font-semibold mb-4">Saved Payment Methods</h2>
          {paymentMethods.length === 0 ? (
            <p className="text-gray-400">No saved payment methods</p>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  className="flex justify-between items-center p-3 bg-gray-800 rounded-lg"
                >
                  <div>
                    <span className="font-medium capitalize">{pm.brand}</span>
                    <span className="text-gray-400 ml-2">****{pm.last4}</span>
                    <span className="text-gray-500 text-sm ml-2">
                      Exp: {pm.exp_month}/{pm.exp_year}
                    </span>
                    {pm.is_default && (
                      <span className="text-green-500 text-xs ml-2">
                        DEFAULT
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeletePaymentMethod(pm.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default DonatorDashboard;